import { COVERAGE } from "./coverage.js";
import { TEXT_FAMILY, TITLE_FAMILY } from "./fonts.js";
import { itemRank, progressOf, type Slide, type StudioDoc } from "./model.js";

/**
 * Draws one slide of the « liste » pilot on a 1080 × 1350 canvas. The preview and the PNG
 * export both call this function, so what is seen is what is exported. Measurements come
 * from the generator's « Opulancia » profile (generateur-carrousel 2.1: .slide, h2, .hl,
 * .grad, .swipe, .progress, .igcard, fitTitle…), ported from its CSS to coordinates; the
 * correspondence is listed in docs/STUDIO.md.
 * Letter spacing is applied character by character (kerning kept), the same in every
 * browser. The arrows and the check mark are drawn as thin paths: neither Anton nor Inter
 * has these characters; the generator got them from whatever fallback font the computer
 * had, so their exact shape varied from one machine to another.
 */
export const WIDTH = 1080;
export const HEIGHT = 1350;
export const COLORS = { background: "#0A0A0A", text: "#FFFFFF", accent: "#E0A84A", onAccent: "#0A0A0A", instagram: "#3897F0" } as const;

const PAD_X = 92;
const PAD_Y = 96;
const CONTENT_W = WIDTH - 2 * PAD_X;
/** Anton's and Inter's vertical metrics (hhea, 2048 units per em). */
interface Metrics {
  ascent: number;
  descent: number;
}
const ANTON: Metrics = { ascent: 2409 / 2048, descent: 674 / 2048 };
const INTER: Metrics = { ascent: 1984 / 2048, descent: 494 / 2048 };
/** Chromium rounds a font's ascent and descent to whole pixels; « normal » line height is their sum. */
const rounded = (m: Metrics, size: number) => ({ a: Math.round(m.ascent * size), d: Math.round(m.descent * size) });
const normalLineHeight = (m: Metrics, size: number) => rounded(m, size).a + rounded(m, size).d;
/** Baseline within a line box (Chromium: the half-leading is floored on the ascent side). */
function baselineIn(lineHeight: number, m: Metrics, size: number): number {
  const { a, d } = rounded(m, size);
  return a + Math.floor((lineHeight - a - d) / 2);
}
/** How far the text's own box goes below its line box (when the line height is smaller than the font's). */
const overflowBelow = (lineHeight: number, m: Metrics, size: number) => Math.max(0, rounded(m, size).d - (lineHeight - baselineIn(lineHeight, m, size)));
const BRAND_SIZE = 30;
const BRAND_TOP = PAD_Y - 26;
const BODY_TOP = BRAND_TOP + normalLineHeight(INTER, BRAND_SIZE);
const BODY_BOTTOM = HEIGHT - PAD_Y;
/** Bottom of the band where the brand is drawn (the image's top must stay calm there). */
export const BRAND_BOTTOM = BODY_TOP;
const GAP = 22;
const CIRCLED_ARROW = { x: WIDTH - PAD_X - 78, y: BODY_BOTTOM - 78, size: 78 };
const SMALL_ARROW = { x: WIDTH - PAD_X - 46, y: BODY_BOTTOM - 54, w: 46, h: 54 };
const CARD = { x: PAD_X, y: BODY_BOTTOM - 150, w: CONTENT_W, h: 150 };

export interface LoadedImage {
  source: CanvasImageSource;
  width: number;
  height: number;
}

export interface RenderAssets {
  images: Map<string, LoadedImage>;
  monogram: LoadedImage | null;
}

export interface RenderReport {
  /** Title size actually used (reduced when the text did not fit). */
  titleSize: number;
  /** For information (the title was reduced). */
  warnings: string[];
  /** What prevents the export: text on the logo or the arrow, missing character or image. */
  blocking: string[];
  /** Where the text block was drawn (y from the top, in px of the 1080 × 1350 slide). */
  text: { top: number; height: number };
}

type Ctx = CanvasRenderingContext2D;

const setFont = (ctx: Ctx, weight: number, size: number, family: string) => {
  ctx.font = `${weight} ${size}px "${family}"`;
};
const chars = (text: string) => [...text];
/** Width of a text with CSS letter-spacing (added after every character, the last one too). */
const spacedWidth = (ctx: Ctx, text: string, spacing: number) => ctx.measureText(text).width + chars(text).length * spacing;

function fillSpaced(ctx: Ctx, text: string, x: number, y: number, spacing: number): void {
  if (!spacing) {
    ctx.fillText(text, x, y);
    return;
  }
  let prefix = "";
  chars(text).forEach((ch, i) => {
    ctx.fillText(ch, x + ctx.measureText(prefix).width + i * spacing, y);
    prefix += ch;
  });
}

/** text-shadow: each shadow is cast by a copy drawn outside the canvas, then the text itself. */
function withShadows(ctx: Ctx, shadows: Array<[number, number, string]>, draw: (dx: number) => void): void {
  const away = 4 * WIDTH;
  for (const [dy, blur, color] of shadows) {
    ctx.save();
    ctx.shadowColor = color;
    ctx.shadowOffsetX = away;
    ctx.shadowOffsetY = dy;
    ctx.shadowBlur = blur;
    draw(-away);
    ctx.restore();
  }
  draw(0);
}

// ---------------------------------------------------------------------------
// Characters the fonts do not have (they would be drawn with another font)
// ---------------------------------------------------------------------------

/** True when the face draws the character itself (its cmap, src/studio/coverage.ts). */
function hasGlyph(face: string, ch: string): boolean {
  const ranges = COVERAGE[face] ?? [];
  const cp = ch.codePointAt(0)!;
  let lo = 0;
  let hi = ranges.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const [a, b] = ranges[mid]!;
    if (cp < a) hi = mid - 1;
    else if (cp > b) lo = mid + 1;
    else return true;
  }
  return false;
}

/** Characters of a text that the face (« anton-400 », « inter-600 »…) does not have. */
export function missingCharacters(text: string, face: string): string[] {
  const out = new Set<string>();
  for (const ch of chars(text)) if (!/\s/.test(ch) && !hasGlyph(face, ch)) out.add(ch);
  return [...out];
}

/**
 * French typography: the space before » ! ? : ; and after « never breaks a line (it becomes
 * a no-break space, same width), so a guillemet or a question mark is never left alone.
 */
export const frenchSpaces = (text: string) => text.replace(/ +([»!?:;])/g, "\u00a0$1").replace(/« +/g, "«\u00a0");

// ---------------------------------------------------------------------------
// Title layout: words, highlighted runs, line breaking
// ---------------------------------------------------------------------------

interface Piece {
  text: string;
  /** Index of the highlighted run it belongs to, or -1. */
  run: number;
  width: number;
}
interface Word {
  pieces: Piece[];
  /** Run of the space that follows the word (a run can span several words). */
  spaceRun: number;
}
interface Segment {
  text: string;
  run: number;
  x: number;
  width: number;
}
interface Line {
  segments: Segment[];
  /** Highlighted fragments (x0, x1), padding included. */
  fragments: Array<[number, number]>;
  width: number;
}
interface TitleLayout {
  lines: Line[];
  size: number;
  lineHeight: number;
  spacing: number;
}

/** Character ranges of the highlighted words, case-insensitive, like the generator's accentize(). */
function highlightRuns(text: string, accent: string[]): Array<[number, number]> {
  const words = accent.map((w) => w.trim().toLocaleUpperCase("fr-FR")).filter(Boolean);
  if (!words.length) return [];
  const re = new RegExp(words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|"), "giu");
  return [...text.matchAll(re)].filter((m) => m[0].length).map((m) => [m.index!, m.index! + m[0].length]);
}

function splitWords(ctx: Ctx, text: string, runs: Array<[number, number]>, spacing: number): Word[][] {
  const runAt = (i: number) => runs.findIndex(([a, b]) => i >= a && i < b);
  const paragraphs: Word[][] = [];
  let offset = 0;
  for (const para of text.split("\n")) {
    const words: Word[] = [];
    let i = 0;
    while (i < para.length) {
      while (i < para.length && para[i] === " ") i++;
      if (i >= para.length) break;
      let j = i;
      while (j < para.length && para[j] !== " ") j++;
      const pieces: Piece[] = [];
      for (let k = i; k < j; ) {
        const run = runAt(offset + k);
        let e = k + 1;
        while (e < j && runAt(offset + e) === run) e++;
        const t = para.slice(k, e);
        pieces.push({ text: t, run, width: spacedWidth(ctx, t, spacing) });
        k = e;
      }
      words.push({ pieces, spaceRun: j < para.length ? runAt(offset + j) : -1 });
      i = j;
    }
    paragraphs.push(words);
    offset += para.length + 1;
  }
  return paragraphs;
}

/**
 * Places the words of one line: every highlighted fragment gets 0.12 em of padding on each
 * side, also where a run is cut by a line break (CSS box-decoration-break: clone).
 */
function placeLine(words: Word[], space: number, pad: number, blocks: boolean): Line {
  const flat: Array<{ text: string; run: number; width: number }> = [];
  words.forEach((w, wi) => {
    flat.push(...w.pieces);
    if (wi < words.length - 1) flat.push({ text: " ", run: w.spaceRun, width: space });
  });
  const segments: Segment[] = [];
  const fragments: Array<[number, number]> = [];
  let x = 0;
  let start = 0;
  flat.forEach((s, i) => {
    const opens = blocks && s.run >= 0 && flat[i - 1]?.run !== s.run;
    const closes = blocks && s.run >= 0 && flat[i + 1]?.run !== s.run;
    if (opens) {
      start = x;
      x += pad;
    }
    segments.push({ text: s.text, run: s.run, x, width: s.width });
    x += s.width;
    if (closes) {
      x += pad;
      fragments.push([start, x]);
    }
  });
  return { segments, fragments, width: x };
}

function wrap(paragraphs: Word[][], maxWidth: number, space: number, pad: number, blocks: boolean): Line[] {
  const lines: Line[] = [];
  for (const para of paragraphs) {
    let current: Word[] = [];
    for (const w of para) {
      if (current.length && placeLine([...current, w], space, pad, blocks).width > maxWidth) {
        lines.push(placeLine(current, space, pad, blocks));
        current = [w];
      } else current.push(w);
    }
    lines.push(placeLine(current, space, pad, blocks));
  }
  return lines;
}

/** text-wrap: balance as Chromium does it: the narrowest width keeping the line count (up to 6 lines). */
function wrapBalanced(paragraphs: Word[][], maxWidth: number, space: number, pad: number, blocks: boolean): Line[] {
  return paragraphs.flatMap((para) => {
    const base = wrap([para], maxWidth, space, pad, blocks);
    if (base.length < 2 || base.length > 6) return base;
    let lo = 0;
    let hi = maxWidth;
    for (let n = 0; n < 20; n++) {
      const mid = (lo + hi) / 2;
      if (wrap([para], mid, space, pad, blocks).length <= base.length) hi = mid;
      else lo = mid;
    }
    return wrap([para], hi, space, pad, blocks);
  });
}

function layoutTitle(ctx: Ctx, slide: Slide, size: number, lineHeight: number, maxWidth: number, balance: boolean): TitleLayout {
  setFont(ctx, 400, size, TITLE_FAMILY);
  const spacing = 0.005 * size;
  const text = frenchSpaces(slide.title).toLocaleUpperCase("fr-FR");
  const blocks = slide.highlight === "block";
  const paragraphs = splitWords(ctx, text, highlightRuns(text, slide.accent.map(frenchSpaces)), spacing);
  const space = spacedWidth(ctx, " ", spacing);
  const lines = (balance ? wrapBalanced : wrap)(paragraphs, maxWidth, space, 0.12 * size, blocks);
  return { lines, size, lineHeight: lineHeight * size, spacing };
}

/** Plain wrapping of the secondary text (forced line breaks kept). */
function wrapPlain(ctx: Ctx, text: string, maxWidth: number): Array<{ text: string; width: number }> {
  const out: Array<{ text: string; width: number }> = [];
  for (const para of text.split("\n")) {
    let current = "";
    for (const w of para.split(/[ \t]+/).filter(Boolean)) {
      const candidate = current ? `${current} ${w}` : w;
      if (current && ctx.measureText(candidate).width > maxWidth) {
        out.push({ text: current, width: ctx.measureText(current).width });
        current = w;
      } else current = candidate;
    }
    out.push({ text: current, width: ctx.measureText(current).width });
  }
  return out;
}

// ---------------------------------------------------------------------------
// The text block (.body): badge, title, secondary text, button
// ---------------------------------------------------------------------------

type Block =
  | { kind: "badge"; h: number; w: number; rank: number }
  | { kind: "title"; h: number; layout: TitleLayout }
  | { kind: "sub"; h: number; lines: Array<{ text: string; width: number }>; size: number }
  | { kind: "button"; h: number; w: number; label: string };

interface Plan {
  blocks: Block[];
  top: number;
  height: number;
  available: number;
  titleSize: number;
}

export function titleStyle(doc: StudioDoc, slide: Slide): { size: number; lineHeight: number; balance: boolean } {
  const hl = slide.highlight === "block";
  const size = slide.titleSize ?? 100;
  if (slide.kind === "cover") return { size, lineHeight: hl ? 1.1 : 0.96, balance: false };
  // numbered items were the generator's « compte à rebours » slides, whose title wraps normally
  const numbered = slide.kind === "item" && doc.settings.numbered;
  return { size, lineHeight: hl ? 1.22 : 1.04, balance: !numbered };
}

/** Width of the button label, whose « → » is drawn as a path (Inter has no arrow). */
const ARROW_ADVANCE = 0.9;
function labelWidth(ctx: Ctx, label: string, size: number): number {
  const parts = label.split("→");
  return parts.reduce((s, p) => s + ctx.measureText(p).width, 0) + (parts.length - 1) * ARROW_ADVANCE * size;
}

function plan(ctx: Ctx, doc: StudioDoc, index: number, maxWidth: number): Plan {
  const slide = doc.slides[index]!;
  const style = titleStyle(doc, slide);
  const card = slide.kind === "end" && slide.objective === "follow";
  const padTop = slide.position === "top" ? 20 : 0;
  const padBottom = card ? 190 : slide.position === "bottom" ? 20 : 0;
  const bodyHeight = BODY_BOTTOM - BODY_TOP;
  const available = bodyHeight - padTop - padBottom;

  const subSize = slide.subSize ?? 35;
  setFont(ctx, 600, subSize, TEXT_FAMILY);
  const subMax = Math.min(maxWidth, 50 * ctx.measureText("0").width); // max-width: 50ch
  const subLines = slide.sub.trim() ? wrapPlain(ctx, frenchSpaces(slide.sub.trim()), subMax) : [];
  let button: Block | null = null;
  if (slide.kind === "end" && slide.objective === "save" && slide.button?.trim()) {
    setFont(ctx, 700, 44, TEXT_FAMILY);
    const label = slide.button.trim();
    button = { kind: "button", h: normalLineHeight(INTER, 44) + 52 + 4, w: labelWidth(ctx, label, 44) + 92 + 4, label };
  }

  const build = (size: number): Block[] => {
    const blocks: Block[] = [];
    if (slide.kind === "item" && doc.settings.numbered) blocks.push({ kind: "badge", h: 150, w: 150, rank: itemRank(doc, index) });
    const layout = layoutTitle(ctx, slide, size, style.lineHeight, maxWidth, style.balance);
    blocks.push({ kind: "title", h: layout.lines.length * layout.lineHeight, layout });
    if (subLines.length) blocks.push({ kind: "sub", h: subLines.length * subSize * 1.4, lines: subLines, size: subSize });
    if (button) blocks.push(button);
    return blocks;
  };
  const total = (blocks: Block[]) => blocks.reduce((s, b) => s + b.h, 0) + GAP * Math.max(0, blocks.length - 1);
  const topOf = (height: number) => (slide.position === "top" ? padTop : slide.position === "center" ? padTop + (available - height) / 2 : bodyHeight - padBottom - height);
  // as fitTitle() measures it (scrollHeight): when the title comes last, the part of its last
  // line's text box hanging below the line box counts too
  const fits = (blocks: Block[]) => {
    const title = blocks.find((b) => b.kind === "title") as Extract<Block, { kind: "title" }>;
    const height = total(blocks);
    const hanging = blocks.at(-1) === title ? overflowBelow(title.layout.lineHeight, ANTON, title.layout.size) : 0;
    // scrollHeight is rounded to a whole pixel, then compared with clientHeight + 1
    return height <= available + 1 && topOf(height) + height + hanging < bodyHeight + 1.5 && title.layout.lines.every((l) => l.width <= maxWidth + 1);
  };

  // the generator's fitTitle(): the title loses 2 px at a time until the block fits, down to 28 px
  let size = style.size;
  let blocks = build(size);
  if (!fits(blocks)) {
    const maxStep = Math.max(0, Math.floor((size - 28) / 2));
    if (fits(build(size - 2 * maxStep))) {
      let lo = 1;
      let hi = maxStep;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (fits(build(size - 2 * mid))) hi = mid;
        else lo = mid + 1;
      }
      size -= 2 * lo;
    } else size -= 2 * maxStep;
    blocks = build(size);
  }
  const height = total(blocks);
  return { blocks, top: BODY_TOP + topOf(height), height, available, titleSize: size };
}

const lineX = (align: Slide["align"], width: number) => (align === "left" ? PAD_X : align === "right" ? WIDTH - PAD_X - width : PAD_X + (CONTENT_W - width) / 2);

interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}
const overlaps = (a: Box, b: Box) => a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;

/** What the text block covers, line by line. */
function contentBoxes(slide: Slide, p: Plan): Box[] {
  const boxes: Box[] = [];
  let y = p.top;
  for (const b of p.blocks) {
    if (b.kind === "title") {
      b.layout.lines.forEach((line, i) => {
        const x = lineX(slide.align, line.width);
        boxes.push({ x0: x, y0: y + i * b.layout.lineHeight, x1: x + line.width, y1: y + (i + 1) * b.layout.lineHeight });
      });
    } else if (b.kind === "sub") {
      const lh = b.size * 1.4;
      b.lines.forEach((line, i) => {
        const x = lineX(slide.align, line.width);
        boxes.push({ x0: x, y0: y + i * lh, x1: x + line.width, y1: y + (i + 1) * lh });
      });
    } else {
      const x = lineX(slide.align, b.w);
      boxes.push({ x0: x, y0: y, x1: x + b.w, y1: y + b.h });
    }
    y += b.h + GAP;
  }
  return boxes;
}

function arrowBox(slide: Slide): Box | null {
  if (!slide.arrow || slide.kind === "end") return null;
  const a = slide.kind === "cover" ? { x: CIRCLED_ARROW.x, y: CIRCLED_ARROW.y, w: CIRCLED_ARROW.size, h: CIRCLED_ARROW.size } : SMALL_ARROW;
  return { x0: a.x - 12, y0: a.y - 6, x1: a.x + a.w, y1: a.y + a.h };
}
const collides = (slide: Slide, p: Plan) => {
  const arrow = arrowBox(slide);
  return !!arrow && contentBoxes(slide, p).some((b) => overlaps(b, arrow));
};

// ---------------------------------------------------------------------------
// The slide
// ---------------------------------------------------------------------------

export function renderSlide(ctx: Ctx, doc: StudioDoc, index: number, assets: RenderAssets): RenderReport {
  const slide = doc.slides[index]!;
  const warnings: string[] = [];
  const blocking: string[] = [];
  ctx.save();
  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";
  ctx.fillStyle = COLORS.background;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  // image, darkening and bottom fade: only over an image, as in the generator
  const image = slide.image ? assets.images.get(slide.image.sha256) : undefined;
  if (slide.image && !image) blocking.push("Image pas encore chargée.");
  if (image) {
    drawCover(ctx, image, slide.fx, slide.fy, slide.zoom);
    if (slide.darken > 0) {
      ctx.fillStyle = `rgba(0,0,0,${slide.darken})`;
      ctx.fillRect(0, 0, WIDTH, HEIGHT);
    }
    if (slide.fade > 0 && slide.fadeStrength > 0) drawFade(ctx, slide.fade, slide.fadeStrength);
  }

  drawLogo(ctx);

  // the text block: full width; narrower if it would run into the arrow
  let p = plan(ctx, doc, index, CONTENT_W);
  if (collides(slide, p)) {
    const q = plan(ctx, doc, index, slide.align === "center" ? CONTENT_W - 2 * 104 : CONTENT_W - 104);
    if (!collides(slide, q)) p = q;
    else blocking.push("Le texte touche la flèche : raccourcis-le ou retire la flèche.");
  }
  if (p.top < BODY_TOP - 1 || p.height > p.available + 1) blocking.push("Texte trop long : il déborde sur le logo. Raccourcis-le ou réduis la taille.");
  const title = p.blocks.find((b) => b.kind === "title") as Extract<Block, { kind: "title" }>;
  if (title.layout.lines.some((l) => l.width > CONTENT_W + 1)) blocking.push("Un mot du titre est plus large que la slide.");
  if (p.titleSize < titleStyle(doc, slide).size) warnings.push(`Titre réduit de ${titleStyle(doc, slide).size} à ${p.titleSize} px pour tenir.`);
  drawBlocks(ctx, slide, p);

  if (slide.arrow && slide.kind === "cover") drawCircledArrow(ctx);
  else if (slide.arrow && slide.kind === "item") drawSmallArrow(ctx);
  if (slide.kind === "end" && slide.objective === "follow") drawCard(ctx, slide.handle || "opulanciamedia", assets.monogram);

  const progress = progressOf(doc, index);
  if (progress !== null) {
    ctx.fillStyle = "rgba(255,255,255,.20)";
    ctx.fillRect(0, 0, WIDTH, 7);
    ctx.fillStyle = COLORS.accent;
    ctx.fillRect(0, 0, (WIDTH * Math.round(progress * 100)) / 100, 7);
  }

  // characters the fonts lack would be drawn with another font: never exported silently
  const missing = new Set([
    ...missingCharacters(slide.title.toLocaleUpperCase("fr-FR"), "anton-400"),
    ...missingCharacters(slide.sub, "inter-600"),
    ...(slide.kind === "end" && slide.objective === "save" ? missingCharacters((slide.button ?? "").replaceAll("→", ""), "inter-700") : []),
    ...(slide.kind === "end" && slide.objective === "follow" ? missingCharacters(slide.handle ?? "", "inter-700") : []),
  ]);
  if (missing.size) blocking.push(`Caractère absent des polices : ${[...missing].join(" ")} (il serait dessiné dans une autre police).`);
  ctx.restore();
  return { titleSize: p.titleSize, warnings, blocking, text: { top: p.top, height: p.height } };
}

/** background-size: cover; background-position: fx% fy%; then scale(zoom) around that same point. */
export function drawCover(ctx: Ctx, image: LoadedImage, fx: number, fy: number, zoom: number): void {
  const s = Math.max(WIDTH / image.width, HEIGHT / image.height);
  const w = image.width * s;
  const h = image.height * s;
  const x = ((WIDTH - w) * fx) / 100;
  const y = ((HEIGHT - h) * fy) / 100;
  const ox = (WIDTH * fx) / 100;
  const oy = (HEIGHT * fy) / 100;
  ctx.drawImage(image.source, ox + (x - ox) * zoom, oy + (y - oy) * zoom, w * zoom, h * zoom);
}

/**
 * .grad: from the bottom, fade × 50 % of the height; stops #000 0–65 %, .75 at 78 %, .35 at 90 %,
 * 0 at 100 %. `strength` scales every stop (1: the generator's fade, unchanged).
 */
export function fadeZone(fade: number): { top: number; solid: number } {
  const h = fade * HEIGHT * 0.5;
  return { top: HEIGHT - h, solid: HEIGHT - h * 0.35 };
}
function drawFade(ctx: Ctx, fade: number, strength: number): void {
  const h = fade * HEIGHT * 0.5;
  const g = ctx.createLinearGradient(0, HEIGHT, 0, HEIGHT - h);
  g.addColorStop(0, `rgba(0,0,0,${strength})`);
  g.addColorStop(0.65, `rgba(0,0,0,${strength})`);
  g.addColorStop(0.78, `rgba(0,0,0,${0.75 * strength})`);
  g.addColorStop(0.9, `rgba(0,0,0,${0.35 * strength})`);
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, HEIGHT - h, WIDTH, h);
}

/** The logo: « OPULANCIA » in Inter 700, 30 px, letter-spacing 0.34 em, centred, opacity 0.92. */
function drawLogo(ctx: Ctx): void {
  setFont(ctx, 700, BRAND_SIZE, TEXT_FAMILY);
  const text = "OPULANCIA";
  const spacing = 0.34 * BRAND_SIZE;
  // centred like the generator's block: the width includes the spacing after the last letter
  const x = PAD_X + (CONTENT_W - spacedWidth(ctx, text, spacing)) / 2;
  const y = BRAND_TOP + rounded(INTER, BRAND_SIZE).a;
  ctx.save();
  ctx.globalAlpha = 0.92;
  ctx.fillStyle = COLORS.text;
  withShadows(ctx, [[2, 10, "rgba(0,0,0,.6)"], [1, 3, "rgba(0,0,0,.55)"]], (dx) => fillSpaced(ctx, text, x + dx, y, spacing));
  ctx.restore();
}

function drawBlocks(ctx: Ctx, slide: Slide, p: Plan): void {
  let y = p.top;
  for (const b of p.blocks) {
    if (b.kind === "badge") drawBadge(ctx, lineX(slide.align, 150), y, b.rank);
    else if (b.kind === "title") drawTitle(ctx, slide, b.layout, y);
    else if (b.kind === "sub") {
      setFont(ctx, 600, b.size, TEXT_FAMILY);
      ctx.fillStyle = COLORS.text;
      const lh = b.size * 1.4;
      b.lines.forEach((line, i) => {
        const x = lineX(slide.align, line.width);
        const baseline = y + i * lh + baselineIn(lh, INTER, b.size);
        withShadows(ctx, [[2, 8, "rgba(0,0,0,.55)"], [1, 3, "rgba(0,0,0,.5)"]], (dx) => ctx.fillText(line.text, x + dx, baseline));
      });
    } else drawButton(ctx, lineX(slide.align, b.w), y, b);
    y += b.h + GAP;
  }
}

/** .rankbadge: 150 px ochre ring (4 px), number in Anton 78 px. */
function drawBadge(ctx: Ctx, x: number, y: number, rank: number): void {
  ctx.strokeStyle = COLORS.accent;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.arc(x + 75, y + 75, 73, 0, Math.PI * 2);
  ctx.stroke();
  setFont(ctx, 400, 78, TITLE_FAMILY);
  ctx.fillStyle = COLORS.accent;
  const label = String(rank);
  // a 78 px line box (line-height 1) centred in the ring
  ctx.fillText(label, x + 75 - ctx.measureText(label).width / 2, y + 75 - 39 + baselineIn(78, ANTON, 78));
}

function drawTitle(ctx: Ctx, slide: Slide, t: TitleLayout, top: number): void {
  const blocks = slide.highlight === "block";
  setFont(ctx, 400, t.size, TITLE_FAMILY);
  const { a, d } = rounded(ANTON, t.size);
  const inLine = baselineIn(t.lineHeight, ANTON, t.size);
  t.lines.forEach((line, i) => {
    const lineTop = top + i * t.lineHeight;
    const baseline = lineTop + inLine;
    const x0 = lineX(slide.align, line.width);
    if (blocks) {
      // .hl: an ochre band 1.06 em high, centred on the text's box, square corners
      const bandTop = baseline - a + (a + d - 1.06 * t.size) / 2;
      ctx.fillStyle = COLORS.accent;
      for (const [a, b] of line.fragments) ctx.fillRect(x0 + a, bandTop, b - a, 1.06 * t.size);
    }
    for (const s of line.segments) {
      if (s.text === " ") continue;
      ctx.fillStyle = s.run >= 0 ? (blocks ? COLORS.onAccent : COLORS.accent) : COLORS.text;
      fillSpaced(ctx, s.text, x0 + s.x, baseline, t.spacing);
    }
  });
}

function strokeArrow(ctx: Ctx, x0: number, x1: number, y: number, head: number, width: number): void {
  ctx.save();
  ctx.lineWidth = width;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(x0, y);
  ctx.lineTo(x1, y);
  ctx.moveTo(x1 - head, y - head);
  ctx.lineTo(x1, y);
  ctx.lineTo(x1 - head, y + head);
  ctx.stroke();
  ctx.restore();
}

/** Cover: a thin arrow in a thin ochre ring, bottom right (78 px, 3 px border). */
function drawCircledArrow(ctx: Ctx): void {
  const { x, y, size } = CIRCLED_ARROW;
  const cx = x + size / 2;
  const cy = y + size / 2;
  ctx.strokeStyle = COLORS.accent;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(cx, cy, size / 2 - 1.5, 0, Math.PI * 2);
  ctx.stroke();
  strokeArrow(ctx, cx - 15, cx + 15, cy, 7.5, 2.4);
}

/** List items: the discreet thin ochre arrow, bottom right, opacity 0.85. */
function drawSmallArrow(ctx: Ctx): void {
  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.strokeStyle = COLORS.accent;
  // where the generator's « → » (54 px, from a fallback font) falls: on the math axis, not centred
  const { x, y, w, h } = SMALL_ARROW;
  strokeArrow(ctx, x + 3, x + w - 11, y + h - 17, 8, 2.6);
  ctx.restore();
}

function roundRect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** .cta, the « Enregistre → » button: ochre outline (2 px), Inter 700 44 px, rounded ends. */
function drawButton(ctx: Ctx, x: number, y: number, b: Extract<Block, { kind: "button" }>): void {
  ctx.strokeStyle = COLORS.accent;
  ctx.lineWidth = 2;
  roundRect(ctx, x + 1, y + 1, b.w - 2, b.h - 2, Math.min(60, (b.h - 2) / 2));
  ctx.stroke();
  setFont(ctx, 700, 44, TEXT_FAMILY);
  ctx.fillStyle = COLORS.accent;
  const baseline = y + 2 + 26 + rounded(INTER, 44).a;
  let cx = x + 2 + 46;
  b.label.split("→").forEach((part, i, parts) => {
    ctx.fillText(part, cx, baseline);
    cx += ctx.measureText(part).width;
    if (i < parts.length - 1) {
      strokeArrow(ctx, cx + 0.2 * 44, cx + 0.74 * 44, baseline - 0.25 * 44, 0.13 * 44, 0.05 * 44);
      cx += ARROW_ADVANCE * 44;
    }
  });
}

/** .igcard: monogram, handle and blue badge, « Suis-nous sur Instagram », blue « Suivre » button. */
function drawCard(ctx: Ctx, handle: string, monogram: LoadedImage | null): void {
  const { x, y, w, h } = CARD;
  ctx.save();
  roundRect(ctx, x + 0.5, y + 0.5, w - 1, h - 1, 24);
  ctx.fillStyle = "rgba(8,7,6,.64)";
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,.15)";
  ctx.lineWidth = 1;
  ctx.stroke();

  // avatar: the embedded monogram, never recoloured
  const ax = x + 1 + 30;
  const ay = y + (h - 96) / 2;
  ctx.save();
  ctx.beginPath();
  ctx.arc(ax + 48, ay + 48, 48, 0, Math.PI * 2);
  ctx.clip();
  ctx.fillStyle = COLORS.accent;
  ctx.fillRect(ax, ay, 96, 96);
  if (monogram) {
    const s = Math.max(96 / monogram.width, 96 / monogram.height);
    ctx.drawImage(monogram.source, ax + (96 - monogram.width * s) / 2, ay + (96 - monogram.height * s) / 2, monogram.width * s, monogram.height * s);
  }
  ctx.restore();

  // « Suivre »
  setFont(ctx, 700, 30, TEXT_FAMILY);
  const bw = ctx.measureText("Suivre").width + 80;
  const bh = normalLineHeight(INTER, 30) + 36;
  const bx = x + w - 1 - 30 - bw;
  const by = y + (h - bh) / 2;
  roundRect(ctx, bx, by, bw, bh, bh / 2);
  ctx.fillStyle = COLORS.instagram;
  ctx.fill();
  ctx.fillStyle = "#FFFFFF";
  ctx.fillText("Suivre", bx + 40, by + 18 + rounded(INTER, 30).a);

  // handle (cut with « … » if too long), badge, caption
  const tx = ax + 96 + 26;
  const handleH = normalLineHeight(INTER, 34);
  const columnTop = y + (h - (handleH + 4 + normalLineHeight(INTER, 26))) / 2;
  setFont(ctx, 700, 34, TEXT_FAMILY);
  const room = bx - 26 - tx - 10 - 26;
  let text = `@${handle}`;
  if (ctx.measureText(text).width > room) {
    while (text.length > 2 && ctx.measureText(`${text}…`).width > room) text = text.slice(0, -1);
    text = `${text}…`;
  }
  ctx.fillText(text, tx, columnTop + rounded(INTER, 34).a);
  const badgeX = tx + ctx.measureText(text).width + 10;
  const badgeCy = columnTop + handleH / 2;
  ctx.fillStyle = COLORS.instagram;
  ctx.beginPath();
  ctx.arc(badgeX + 13, badgeCy, 13, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = 2.6;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(badgeX + 8, badgeCy + 0.5);
  ctx.lineTo(badgeX + 11.6, badgeCy + 4);
  ctx.lineTo(badgeX + 18.2, badgeCy - 3.6);
  ctx.stroke();
  setFont(ctx, 400, 26, TEXT_FAMILY);
  ctx.fillStyle = "rgba(255,255,255,.62)";
  ctx.fillText("Suis-nous sur Instagram", tx, columnTop + handleH + 4 + rounded(INTER, 26).a);
  ctx.restore();
}
